import { ProjectPageContents } from "components/front/project/ProjectPageContents";
import { ExhibitionProject } from "interfaces/front/business";
import CookieHandler from "lib/cookie";
import { FrontAuthProps, withFrontAuth } from "lib/hoc/withFrontAuth";
import { useRealtimeTippingCommentContext, withRealtimeTippingComment } from "lib/hoc/withRealtimeTippingComment";
import { useCourses } from "lib/hooks/useCourses";
import { useProjectPageVariables } from "lib/hooks/useProjectPageVariables";
import { FrontProjectService as ClientService } from "lib/service/client/FrontProjectService";
import { FrontProjectService as NodeService } from "lib/service/node/FrontProjectService";
import { infoToast } from "lib/toast";
import { GetServerSideProps, NextPage } from "next";
import React, { useEffect, useMemo } from "react";
import ProjectHead from "components/front/head/ProjectHead";
import { useViewProject } from "lib/hooks/front/useEcommerceEventTrigger";

type Props = {
  slug: string;
  project: ExhibitionProject;
  notice: string;
};

const ProjectPage: NextPage<Props & FrontAuthProps> = ({ project, slug, notice }: Props) => {
  const service = useMemo(() => new ClientService(), []);
  const [rewardCourses, donationCourse, aniale] = useCourses(service, slug);
  const variables = useProjectPageVariables(slug);
  const { setRealtimeCommentInterval } = useRealtimeTippingCommentContext();
  useEffect(() => {
    const interval = variables?.realtime_comment_interval;
    if (interval) {
      setRealtimeCommentInterval(interval);
    }
  }, [variables?.realtime_comment_interval]);
  useViewProject(project);
  useEffect(() => {
    if (notice) {
      infoToast(notice);
    }
  }, []);
  return (
    <>
      <ProjectHead project={project} />
      <ProjectPageContents
        project={project}
        slug={slug}
        rewardCourses={rewardCourses}
        donationCourse={donationCourse}
        aniale={aniale}
        summary={variables?.summary}
        monumentNames={variables?.monument_names}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
  const slug = (context.params?.slug as string) || "";
  const service = new NodeService();
  const result = await service.get(slug);
  const cookieHandler = new CookieHandler(context);
  const notice = cookieHandler.oneTimeNotice();
  context.res.setHeader("Cache-Control", "s-maxage=3600,max-age=5,must-revalidate");
  if (result.isSuccess()) {
    const project = result.value as ExhibitionProject;
    return {
      props: {
        slug,
        project,
        notice,
      },
    };
  } else {
    return {
      props: { slug: "", notice, project: {} as ExhibitionProject },
      unstable_redirect: {
        permanent: false,
        destination: "/",
      },
    };
  }
};

export default withFrontAuth(withRealtimeTippingComment(ProjectPage));
